import { useCallback, useMemo, useState } from "react";
import ActiveSkillCard from "@/components/ActiveSkillCard";
import DamageCalculationForm from "@/components/forms/DamageCalculationForm";
import LeaderSkillForm from "@/components/forms/LeaderSkillForm";
import LinkSkillForm from "@/components/forms/LinkSkillForm";
import PassiveSkillForm from "@/components/forms/PassiveSkillForm";
import StatsForm from "@/components/forms/StatsForm";
import SuperAttackForm from "@/components/forms/SuperAttackForm";
import SupportForm from "@/components/forms/SupportForm";
import ResultDisplay from "@/components/ResultDisplay";
import { DEFAULT_DOKKAN_STATS } from "@/constants/dokkan";
import {
	calculateActiveSkillATK,
	calculateFullStats,
} from "@/lib/calculations";
import type {
	DokkanStats,
	FullCalculationResult,
	ValidationError,
} from "@/lib/types";
import { DamageCalculationInputSchema, validateDokkanStats } from "@/lib/types";
import { calculateStatMultipliers, debounce } from "@/lib/utils";

export default function App() {
	const [stats, setStats] = useState<DokkanStats>(DEFAULT_DOKKAN_STATS);
	const [damageCalcEnabled, setDamageCalcEnabled] = useState(false);
	const [enemyAtk, setEnemyAtk] = useState(50000);
	const [damageReduction, setDamageReduction] = useState(0);
	const [useActiveSkill, setUseActiveSkill] = useState(false);
	const [activeSkillPower, setActiveSkillPower] = useState(5.5);
	const [temporaryATKBoost, setTemporaryATKBoost] = useState(0);
	const [_, setCalculationTrigger] = useState(0);

	const triggerCalculation = useCallback(
		debounce(() => setCalculationTrigger((prev) => prev + 1), 300),
		[],
	);

	const handleStatChange = useCallback(
		(field: keyof DokkanStats, value: number) => {
			setStats((prev) => ({
				...prev,
				[field]: value,
			}));
			triggerCalculation();
		},
		[triggerCalculation],
	);

	const handleDamageCalcChange = useCallback(
		(field: "enemyAtk" | "damageReduction", value: number) => {
			if (field === "enemyAtk") {
				setEnemyAtk(value);
			} else {
				setDamageReduction(value);
			}
			triggerCalculation();
		},
		[triggerCalculation],
	);

	const handleActiveSkillChange = useCallback(
		(field: "activeSkillPower" | "temporaryATKBoost", value: number) => {
			if (field === "activeSkillPower") {
				setActiveSkillPower(value);
			} else {
				setTemporaryATKBoost(value);
			}
			triggerCalculation();
		},
		[triggerCalculation],
	);

	let calculationResult = useMemo((): FullCalculationResult | null => {
		const parsedDamageInput = DamageCalculationInputSchema.safeParse({
			enemy_atk: enemyAtk,
			damage_reduction: damageReduction,
			player_def: 0,
		});
		const damageInput =
			damageCalcEnabled && parsedDamageInput.success
				? parsedDamageInput.data
				: undefined;

		return calculateFullStats(stats, damageInput);
	}, [stats, damageCalcEnabled, enemyAtk, damageReduction]);

	const validationErrors = useMemo(
		() =>
			validateDokkanStats(stats, {
				enemy_atk: enemyAtk,
				damage_reduction: damageReduction,
				player_def: calculationResult?.def ?? 0,
			}),
		[stats, enemyAtk, damageReduction, calculationResult],
	);

	if (validationErrors.length > 0) {
		calculationResult = {
			atk: 0,
			def: 0,
			atkOverflow: false,
			defOverflow: false,
		};
		if (damageCalcEnabled) {
			calculationResult.damage_calculation = {
				damage_taken: 0,
				damage_reduction_percentage: damageReduction,
			};
		}
	}

	const activeSkillATK = useMemo((): {
		value: number;
		overflow: boolean;
	} | null => {
		if (!useActiveSkill) {
			return null;
		}

		if (validationErrors.length > 0) {
			return { value: 0, overflow: false };
		}

		return calculateActiveSkillATK(
			stats,
			activeSkillPower,
			temporaryATKBoost,
			stats.SAboost_level,
		);
	}, [
		stats,
		activeSkillPower,
		temporaryATKBoost,
		useActiveSkill,
		validationErrors,
	]);

	const { statsMultipliers } = useMemo(
		() => calculateStatMultipliers(stats),
		[stats],
	);

	const getErrorsFromPrefix = (prefixes: string[]): ValidationError[] => {
		return validationErrors.filter((error) =>
			prefixes.some((prefix) => error.field.startsWith(prefix)),
		);
	};

	const ErrorDisplay = ({ errors }: { errors: ValidationError[] }) => {
		if (errors.length === 0) return null;

		return (
			<div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
				<ul className="text-sm text-red-700 space-y-1">
					{errors.map((error) => (
						<li key={`${error.field}-${error.message}`}>• {error.message}</li>
					))}
				</ul>
			</div>
		);
	};

	return (
		<div className="min-h-screen bg-gray-50">
			<header className="bg-white shadow-sm">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
					<div className="flex items-center justify-between">
						<h1 className="text-3xl font-bold text-gray-900">ドカバト計算機</h1>
					</div>
				</div>
			</header>

			<main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
					<div className="lg:col-span-2 space-y-6">
						<div>
							<StatsForm stats={stats} onChange={handleStatChange} />
							<ErrorDisplay errors={getErrorsFromPrefix(["stats_"])} />
						</div>
						<div>
							<LeaderSkillForm stats={stats} onChange={handleStatChange} />
							<ErrorDisplay errors={getErrorsFromPrefix(["Lskill_"])} />
						</div>
						<div>
							<PassiveSkillForm stats={stats} onChange={handleStatChange} />
							<ErrorDisplay errors={getErrorsFromPrefix(["Pskill_"])} />
						</div>
						<div>
							<SupportForm stats={stats} onChange={handleStatChange} />
							<ErrorDisplay errors={getErrorsFromPrefix(["support_"])} />
						</div>
						<div>
							<SuperAttackForm
								stats={stats}
								handleStatChange={handleStatChange}
								useActiveSkill={useActiveSkill}
								setUseActiveSkill={setUseActiveSkill}
							/>
							<ErrorDisplay
								errors={getErrorsFromPrefix([
									"SA_",
									"SAboost_level",
									"ki_bonus",
								])}
							/>
						</div>
						{useActiveSkill && (
							<ActiveSkillCard
								activeSkillPower={activeSkillPower}
								temporaryATKBoost={temporaryATKBoost}
								onChange={handleActiveSkillChange}
							/>
						)}
						<div>
							<LinkSkillForm stats={stats} onChange={handleStatChange} />
							<ErrorDisplay errors={getErrorsFromPrefix(["link_"])} />
						</div>
						<DamageCalculationForm
							enemyAtk={enemyAtk}
							damageReduction={damageReduction}
							onChange={handleDamageCalcChange}
							enabled={damageCalcEnabled}
							onToggle={setDamageCalcEnabled}
						/>
						<ErrorDisplay errors={getErrorsFromPrefix(["enemy_", "damage_"])} />
					</div>
					<div className="lg:col-span-1">
						<div className="sticky top-8 space-y-6">
							<ResultDisplay
								result={calculationResult}
								isCalculating={false}
								statsMultipliers={statsMultipliers}
								activeSkillATK={activeSkillATK}
							/>
						</div>
					</div>
				</div>
			</main>

			<footer className="bg-white border-t border-gray-200 mt-16">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
					<p className="text-center mb-3">
						<a
							href="https://marshmallow-qa.com/153039i5d8igcfv?t=xJSuAu&utm_medium=url_text&utm_source=promotion"
							target="_blank"
							rel="noopener noreferrer"
							className="text-sm text-blue-600 underline hover:text-blue-800"
						>
							ご意見・不具合報告はこちらから
						</a>
					</p>
					<p className="text-center text-sm text-gray-500">
						本アプリは「ドラゴンボールZ
						ドッカンバトル」の非公式ファンツールです。
						<br />
						運営会社とは一切関係ありません。
					</p>
				</div>
			</footer>
		</div>
	);
}
